import React, { useEffect, useState } from 'react';
import styles from '../../utils/styles/components/AvailabilityCalendar/style.module.css';

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const monthFormatter = new Intl.DateTimeFormat('fr-FR', {
  month: 'long',
  year: 'numeric'
});

function toStartOfDay(input) {
  const date = new Date(input);
  date.setHours(0, 0, 0, 0);
  return date;
}

function toDateKey(date) {
  return date.toISOString().slice(0, 10);
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function addMonths(date, amount) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function diffInMonths(start, end) {
  return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
}

function enumerateDays(start, end) {
  const dates = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

function normalizeBlockedDays(shows, availabilityBlocks) {
  const blockedDays = new Map();

  availabilityBlocks.forEach((block) => {
    const start = toStartOfDay(block.date_de_debut);
    const end = toStartOfDay(block.date_de_fin);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return;
    }

    enumerateDays(start, end).forEach((date) => {
      blockedDays.set(toDateKey(date), {
        type: 'manual',
        label: block.titre,
        note: block.note || ''
      });
    });
  });

  shows.forEach((show) => {
    const start = toStartOfDay(show.dateStart);
    const end = toStartOfDay(show.dateEnd || show.dateStart);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return;
    }

    enumerateDays(start, end).forEach((date) => {
      blockedDays.set(toDateKey(date), {
        type: 'show',
        label: show.showTitle || 'Représentation',
        note: show.city || ''
      });
    });
  });

  return blockedDays;
}

function getMonthsToRender(shows, availabilityBlocks, minimumMonths) {
  const today = toStartOfDay(new Date());
  const startMonth = startOfMonth(today);
  const endingCandidates = [];

  shows.forEach((show) => {
    if (show?.dateEnd || show?.dateStart) {
      endingCandidates.push(toStartOfDay(show.dateEnd || show.dateStart));
    }
  });

  availabilityBlocks.forEach((block) => {
    if (block?.date_de_fin) {
      endingCandidates.push(toStartOfDay(block.date_de_fin));
    }
  });

  const latestRelevantDate = endingCandidates
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((left, right) => right.getTime() - left.getTime())[0];

  if (!latestRelevantDate) {
    return minimumMonths;
  }

  return Math.min(Math.max(minimumMonths, diffInMonths(startMonth, startOfMonth(latestRelevantDate)) + 1), 12);
}

function buildMonthDays(monthDate) {
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);
  const firstDayIndex = (monthStart.getDay() + 6) % 7;
  const days = [];

  for (let index = 0; index < firstDayIndex; index += 1) {
    days.push(null);
  }

  for (let day = 1; day <= monthEnd.getDate(); day += 1) {
    days.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
  }

  while (days.length % 7 !== 0) {
    days.push(null);
  }

  return days;
}

function getMonthPositionClass(delta, stylesMap) {
  if (delta === 0) {
    return stylesMap.monthActive;
  }

  return delta < 0 ? stylesMap.monthNearLeft : stylesMap.monthNearRight;
}

export const AvailabilityCalendar = ({
  availabilityBlocks = [],
  shows = [],
  minimumMonths = 6
}) => {
  const today = toStartOfDay(new Date());
  const blockedDays = normalizeBlockedDays(shows, availabilityBlocks);
  const monthCount = getMonthsToRender(shows, availabilityBlocks, minimumMonths);
  const months = Array.from({ length: monthCount }, (_, index) => addMonths(startOfMonth(today), index));
  const [activeMonthIndex, setActiveMonthIndex] = useState(0);

  useEffect(() => {
    setActiveMonthIndex((current) => Math.min(current, Math.max(months.length - 1, 0)));
  }, [months.length]);

  const canGoPrevious = activeMonthIndex > 0;
  const canGoNext = activeMonthIndex < months.length - 1;
  const visibleMonths = months
    .map((monthDate, monthIndex) => ({
      monthDate,
      monthIndex,
      delta: monthIndex - activeMonthIndex
    }))
    .filter(({ delta }) => Math.abs(delta) <= 1);

  return (
    <div className={styles.shell}>
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchAvailable}`} />
          <span>Disponible</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchManual}`} />
          <span>Indisponible</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.legendSwatchShow}`} />
          <span>En représentation</span>
        </div>
      </div>

      <div className={styles.stageShell}>
        <button
          aria-label="Voir le mois précédent"
          className={styles.navButton}
          disabled={!canGoPrevious}
          onClick={() => setActiveMonthIndex((current) => Math.max(current - 1, 0))}
          type="button"
        >
          <span aria-hidden="true">←</span>
        </button>

        <div className={styles.stageViewport}>
          <div className={styles.stageTrack}>
            <span aria-hidden="true" className={`${styles.stackGhost} ${styles.stackGhostLeft}`} />
            <span aria-hidden="true" className={`${styles.stackGhost} ${styles.stackGhostRight}`} />

            {visibleMonths.map(({ monthDate, monthIndex, delta }) => {
              const monthDays = buildMonthDays(monthDate);
              const positionClass = getMonthPositionClass(delta, styles);

              return (
                <section
                  aria-hidden={delta !== 0}
                  className={`${styles.monthCard} ${positionClass}`}
                  key={monthDate.toISOString()}
                >
                  <header className={styles.monthHeader}>
                    <h4 className={styles.monthTitle}>
                      {monthFormatter.format(monthDate).replace(/^\w/, (letter) => letter.toUpperCase())}
                    </h4>
                  </header>

                  <div className={styles.weekdays}>
                    {WEEKDAY_LABELS.map((label) => (
                      <span className={styles.weekday} key={label}>
                        {label}
                      </span>
                    ))}
                  </div>

                  <div className={styles.daysGrid}>
                    {monthDays.map((date, index) => {
                      if (!date) {
                        return <span aria-hidden="true" className={styles.dayPlaceholder} key={`empty-${index}`} />;
                      }

                      const dateKey = toDateKey(date);
                      const status = blockedDays.get(dateKey);
                      const isPast = date < today;
                      const titleParts = [];

                      if (status?.type === 'show') {
                        titleParts.push('En représentation');
                      } else if (status?.type === 'manual') {
                        titleParts.push('Indisponible');
                      } else {
                        titleParts.push('Disponible');
                      }

                      if (status?.label) {
                        titleParts.push(status.label);
                      }

                      if (status?.note) {
                        titleParts.push(status.note);
                      }

                      return (
                        <div
                          className={`${styles.day} ${status?.type === 'show' ? styles.dayShow : ''} ${status?.type === 'manual' ? styles.dayManual : ''} ${!status ? styles.dayAvailable : ''} ${isPast ? styles.dayPast : ''}`}
                          key={dateKey}
                          title={titleParts.join(' · ')}
                        >
                          <span className={styles.dayNumber}>{date.getDate()}</span>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </div>

        <button
          aria-label="Voir le mois suivant"
          className={styles.navButton}
          disabled={!canGoNext}
          onClick={() => setActiveMonthIndex((current) => Math.min(current + 1, months.length - 1))}
          type="button"
        >
          <span aria-hidden="true">→</span>
        </button>
      </div>

      <div className={styles.monthMeta}>
        <span>{activeMonthIndex + 1}</span>
        <span className={styles.monthMetaDivider}>/</span>
        <span>{months.length}</span>
      </div>
    </div>
  );
};
